import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Roles } from '@app/core/guard/Roles';
import { ModalCreateLoanOperationOriginComponent } from '@features/dashboard/costs/debt-management/components/modal-create-loan-operation-origin/modal-create-loan-operation-origin.component';
import { LoanOperationOrigin } from '@interfaces/loan/loan-operation-origin.response';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { FinancialInstitutionService } from '@services/http/financial-institution.service';
import { LoanOperationOriginHttpService } from '@services/http/loanOperation/loan-operation-origin-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { TableAction } from '@src/app/shared/components/table/table.component';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { Subject, finalize, takeUntil } from 'rxjs';

@Component({
  selector: 'app-loan-operation-origins',
  standalone: true,
  imports: [CommonModule, RouterModule, SharedModule, ModalCreateLoanOperationOriginComponent],
  templateUrl: './loan-operation-origins.component.html',
  styleUrls: ['./loan-operation-origins.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoanOperationOriginsComponent extends VaultBaseComponent implements OnInit, OnDestroy {
  @ViewChild('modalCreateOrigin') modalCreateOrigin?: ModalCreateLoanOperationOriginComponent;

  financialInstitutionId: string | null = null;
  financialInstitutionName: string = '';
  origins: LoanOperationOrigin[] = [];
  override isLoading = false;
  isAdmin = false;
  override Roles = Roles;
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };

  columnDefinitions: ColumnDefinition[] = [];

  tableOptions: MenuItem[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    translateService: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private loanOperationOriginHttpService: LoanOperationOriginHttpService,
    private financialInstitutionService: FinancialInstitutionService,
    private utilsService: UtilsService,
    private toastHelper: ToastHelperService,
    private formErrorHandler: FormErrorHandlerService,
    private permissionsService: PermissionsService,
    private confirmationService: ConfirmationService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    // Configurar columnDefinitions
    this.columnDefinitions = [
      new ColumnDefinition({
        field: 'country',
        header: this.t(this.tk.Management.Country),
        sortable: true,
      }),
      new ColumnDefinition({
        field: 'city',
        header: this.t(this.tk.Management.City),
        sortable: true,
      }),
    ];

    // Configurar tableOptions
    this.tableOptions = [
      {
        label: this.t(this.tk.General.Update),
        icon: 'pi pi-pencil',
        action: 'edit',
      },
      {
        label: this.t(this.tk.General.Delete),
        icon: 'pi pi-trash',
        action: 'delete',
      },
    ];

    // Configurar breadcrumbs
    this.breadcrumbs = [
      { label: this.t(this.tk.General.Administration), routerLink: '/' },
      {
        label: this.t(this.tk.Management.FinancialInstitutions),
        routerLink: '/dashboard/financial-institution/list',
      },
      {
        label: this.t(this.tk.Management.ManageExternalOrigins),
        routerLink: '',
      },
    ];

    // Verificar se o usuário é ADMIN
    this.permissionsService.hasRole(Roles.ADMIN).subscribe(isAdmin => {
      this.isAdmin = isAdmin;
      this.changeDetectorRef.markForCheck();
    });

    // Obter o ID da instituição financeira da rota
    this.route.params.subscribe(params => {
      this.financialInstitutionId = params['id'] || null;
      if (this.financialInstitutionId) {
        this.loadFinancialInstitution();
        this.loadOrigins();
      }
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadFinancialInstitution(): void {
    if (!this.financialInstitutionId) return;

    this.utilsService.setIsLoading(true);
    this.financialInstitutionService
      .getPermissionGroupById(this.financialInstitutionId)
      .pipe(
        finalize(() => {
          this.utilsService.setIsLoading(false);
          this.changeDetectorRef.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: institution => {
          if (institution) {
            this.financialInstitutionName =
              institution.displayName || institution.institutionName || '';
            this.changeDetectorRef.markForCheck();
          }
        },
        error: error => {
          console.error('Erro ao carregar instituição financeira:', error);
          this.formErrorHandler.showToast(
            error,
            this.t(this.tk.Management.ErrorLoadingFinancialInstitution)
          );
        },
      });
  }

  loadOrigins(): void {
    if (!this.financialInstitutionId) return;

    this.isLoading = true;
    this.loanOperationOriginHttpService
      .getByFinancialInstitution(this.financialInstitutionId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: origins => {
          this.origins = origins || [];
          this.changeDetectorRef.markForCheck();
        },
        error: error => {
          console.error('Erro ao carregar origens:', error);
          this.formErrorHandler.showToast(error, this.t(this.tk.Management.ErrorLoadingOrigins));
        },
      });
  }

  handleTableAction(action: TableAction<LoanOperationOrigin>): void {
    if (action.action === 'edit') {
      this.editOrigin(action.item);
    } else if (action.action === 'delete') {
      this.deleteOrigin(action.item);
    }
  }

  openCreateModal(): void {
    if (!this.financialInstitutionId) {
      this.toastHelper.showToastError(this.t(this.tk.Management.ErrorLoadingFinancialInstitution));
      return;
    }

    this.modalCreateOrigin?.open();
  }

  editOrigin(origin: LoanOperationOrigin): void {
    if (!this.financialInstitutionId) {
      this.toastHelper.showToastError(this.t(this.tk.Management.ErrorLoadingFinancialInstitution));
      return;
    }

    // Definir os dados iniciais da modal
    this.modalCreateOrigin!.initialData = origin;
    this.modalCreateOrigin?.open();
  }

  deleteOrigin(origin: LoanOperationOrigin): void {
    const originLabel = `${origin.country}${origin.city ? ' - ' + origin.city : ''}`;
    this.confirmationService
      .confirm(
        this.t(this.tk.Management.DeleteOrigin),
        this.t(this.tk.Management.ConfirmDeleteOrigin).replace('{0}', originLabel)
      )
      .subscribe(confirmed => {
        if (confirmed) {
          this.performDelete(origin);
        }
      });
  }

  private performDelete(origin: LoanOperationOrigin): void {
    if (!origin.id) return;

    this.utilsService.setIsLoading(true);
    this.loanOperationOriginHttpService
      .delete(origin.id)
      .pipe(
        finalize(() => {
          this.utilsService.setIsLoading(false);
          this.changeDetectorRef.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.toastHelper.showToastSuccess(this.t(this.tk.Management.OriginDeletedSuccessfully));
          this.loadOrigins();
        },
        error: error => {
          console.error('Erro ao excluir origem:', error);
          this.formErrorHandler.showToast(error, this.t(this.tk.Management.ErrorDeletingOrigin));
        },
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleOriginCreated(_origin: LoanOperationOrigin): void {
    this.loadOrigins();
    // Limpar dados iniciais da modal após salvar
    if (this.modalCreateOrigin) {
      this.modalCreateOrigin.initialData = null;
    }
  }

  goBack(): void {
    this.router.navigate(['dashboard/financial-institution/list']);
  }
}
